import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CurrentHouseProvider } from '@/context/CurrentHouseContext';
import { useCanSeeArchive } from '@/hooks/useCanSeeArchive';
import Archive, {
	ArchiveOrigins,
	ArchiveViews,
	type ArchiveOrigin,
	type ArchiveView,
} from '@/components/archive/Archive';

const VIEWS = new Set<string>(Object.values(ArchiveViews));
const ORIGINS = new Set<string>(Object.values(ArchiveOrigins));

/**
 * The same shape as `WithPropertyRole`, for a rule that is a role rather than a permission: the
 * Archive is the owner's, and a reader typing the URL is sent where every other refused route
 * sends them.
 */
const GuardedArchive = () => {
	const { view, from } = Route.useSearch();
	const canSeeArchive = useCanSeeArchive();
	const navigate = useNavigate();

	useEffect(() => {
		if (!canSeeArchive) void navigate({ to: '/dashboard', replace: true });
	}, [canSeeArchive, navigate]);

	if (!canSeeArchive) return null;
	return <Archive initialView={view} origin={from} />;
};

const ArchivePage = () => (
	<CurrentHouseProvider>
		<GuardedArchive />
	</CurrentHouseProvider>
);

export const Route = createFileRoute({
	component: ArchivePage,
	validateSearch: (search: Record<string, unknown>) => ({
		/** The view to open on, so a link from Contacts lands on contacts rather than on tasks. */
		view: VIEWS.has(String(search.view)) ? (search.view as ArchiveView) : undefined,
		/** Where the reader came from, which is where Back returns them. */
		from: ORIGINS.has(String(search.from)) ? (search.from as ArchiveOrigin) : undefined,
	}),
});
